import { forwardRef, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { setAlpha } from '../../../design-tokens';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useKeyboardTooltip } from '../../../hooks/useKeyboardTooltip';
import { renderDesignTokens, useDesignTokens } from '../../../theme';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { Label } from '../../Label';
import { Text } from '../../Text';
import cn from './RadioButton.css.json';
import './RadioButton.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useHtmlId = createUseHtmlId('RadioButton');
/**
 * @internal
 */
export const UnthemedRadioButton = /*#__PURE__*/forwardRef((props, ref) => {
  const [inputId, labelId, descriptionId] = useHtmlId(3);
  const radioInput = useRef(null);
  const [isFocusVisible, setIsFocusVisible] = useState(false);
  const handleFocus = () => {
    radioInput.current?.focus();
  };
  const handleInputFocus = () => {
    setIsFocusVisible(true);
    props.onFocus?.();
  };
  const handleInputBlur = () => {
    setIsFocusVisible(false);
  };
  const handleKeyDown = event => {
    // only spacebar
    if (event.key === ' ') {
      props.onPress?.();
    }
  };
  const rbRef = useRef(null);
  const dt = useDesignTokens();

  // line-height patch specific to radio button to help keep radio button and its label centered
  // NOTE: this will break if the unit is not px
  const labelFontSizeInPixels = parseFloat(dt.enableNovaTokens ? dt.tokens['font-size-body-text-m'] : dt.reportTokens.font.size.m) * 16;
  const labelHeight = Math.round(labelFontSizeInPixels * (dt.enableNovaTokens ? parseFloat(dt.tokens['font-space-line-height-s']) : dt.reportTokens.font.lineHeight));

  // the 14 is the height of the checkbox in pixels. Only define the custom margin if a label is supplied.
  const marginTopCalc = props.label ? `${Math.floor((labelHeight - 14) / 2)}px` : undefined;
  const setKeyboardTooltipRef = useKeyboardTooltip({
    startElementRef: rbRef,
    selectors: [`.${cn.label} label`]
  });
  return /*#__PURE__*/_jsxs("div", {
    className: cx(props.className),
    style: props.style,
    "data-testid": props.testId,
    children: [/*#__PURE__*/_jsxs("div", {
      ref: element => {
        rbRef.current = element;
        setRef(ref, element);
      },
      className: cx(cn.normal, props.selected && cn.selected, props.disabled && cn.disabled, props.readOnly && cn.readOnly, !props.label && cn.noLabel, isFocusVisible && cn.focusVisible),
      tabIndex: !isFocusVisible && props.focusable ? 0 : -1,
      onFocus: handleFocus,
      onMouseDown: props.onPress,
      title: props.label ? undefined : props.tooltip,
      children: [/*#__PURE__*/_jsx("span", {
        className: cx(cn.inputContainer, props.className),
        style: {
          marginTop: marginTopCalc
        },
        children: /*#__PURE__*/_jsx("input", {
          id: inputId,
          ref: el => {
            setKeyboardTooltipRef(el);
            setRef(radioInput, el);
          },
          className: cn.input,
          value: props.value,
          name: props.groupName,
          type: "radio",
          style: {
            position: 'absolute',
            width: '100%',
            height: '100%',
            opacity: 0
          },
          tabIndex: isFocusVisible ? 0 : -1,
          onChange: props.onPress ?? (() => {}),
          onFocus: handleInputFocus,
          onBlur: handleInputBlur,
          role: "radio",
          "data-testid": postfixTestId(props.testId, 'radio'),
          "aria-disabled": props.disabled,
          "aria-checked": props.selected,
          "aria-labelledby": props.label ? `${labelId} ${props.ariaLabelledBy ?? ''}`.trim() : props.ariaLabelledBy,
          "aria-label": props.ariaLabel,
          "aria-describedby": props.description && descriptionId,
          checked: props.selected,
          onKeyDown: handleKeyDown
        })
      }), props.label ? /*#__PURE__*/_jsx(Label, {
        id: labelId,
        disabled: props.disabled,
        className: cn.label,
        wrapText: props.wrapLabel ?? true,
        tokens: () => ({
          fg: dt.tokens['color-body-text']
        }),
        children: props.label
      }) : null]
    }), props.description ? /*#__PURE__*/_jsx("div", {
      className: cn.description,
      children: /*#__PURE__*/_jsx(Text, {
        id: descriptionId,
        disabled: props.disabled,
        className: cx(cn.descriptionText, props.disabled && cn.disabled),
        wrapText: props.wrapDescription ?? true,
        children: props.description
      })
    }) : null]
  });
});

/**
 * @internal
 */
export const RadioButton = /*#__PURE__*/forwardRef((props, ref) => {
  const dt = useDesignTokens();
  const tokens = useMemo(() => getRadioButtonDesignTokens(dt), [dt]);
  return /*#__PURE__*/_jsx(UnthemedRadioButton, {
    ref: ref,
    ...props,
    style: {
      ...tokens,
      ...props.style
    }
  });
});
export const getRadioButtonDesignTokens = dt => {
  /*
   * List sets these tokens at the root of the List for performance considerations.
   * Without knowledge of the state of each radio button in the list, it needs a separate token
   * for each state.
   */
  return {
    ...renderDesignTokens({
      'spacing-s': dt.tokens['space-base-s'],
      'spacing-xs': dt.tokens['space-base-xs'],
      'spacing-xxs': dt.tokens['space-base-xxs'],
      fg: dt.tokens['color-action-text-control-normal'],
      bg: dt.tokens['color-action-background-on-off-normal'],
      bd: dt.tokens['color-action-border-on-off-normal'],
      'font-size-s': dt.tokens['font-size-body-text-xs'],
      'description-fg': dt.tokens['color-label-text'],
      'trans-duration': dt.tokens['duration-200'],
      'trans-tf-ease-in-out': dt.tokens['ease-in-out'],
      'trans-tf-ease-in': dt.tokens['ease-in'],
      'trans-tf-ease-out': dt.tokens['ease-out']
    }, 'radio'),
    ...renderDesignTokens({
      fg: dt.tokens['color-action-text-on-off-selected'],
      bg: dt.tokens['color-action-background-on-off-selected'],
      bd: dt.tokens['color-action-border-on-off-selected']
    }, 'radio-selected'),
    ...renderDesignTokens({
      fg: dt.tokens['color-action-text-on-off-disabled'],
      bg: dt.tokens['color-action-background-on-off-disabled'],
      bd: dt.tokens['color-action-border-on-off-disabled']
    }, 'radio-disabled'),
    ...renderDesignTokens({
      fg: dt.tokens['color-action-text-on-off-selected-disabled'],
      bg: dt.tokens['color-action-background-on-off-selected-disabled'],
      bd: dt.tokens['color-action-border-on-off-selected-disabled']
    }, 'radio-disabled-selected'),
    ...renderDesignTokens({
      fg: dt.tokens['color-action-icon-on-off-read-only'],
      bd: dt.tokens['color-action-border-on-off-read-only'],
      bg: dt.tokens['color-action-background-on-off-read-only']
    }, 'radio-read-only'),
    ...(!dt.enableNovaTokens && getRadioButtonLegacyDesignTokens(dt))
  };
};
export const getRadioButtonLegacyDesignTokens = dt => {
  let readOnlyTokens = {
    fg: dt.c.n_3,
    bg: dt.c.white,
    bd: dt.c.n2
  };
  switch (dt.themeVariant) {
    case 'dark':
      readOnlyTokens = {
        fg: dt.c.white,
        bg: dt.c.p_3,
        bd: setAlpha(dt.c.white, 0.2)
      };
      break;
    case 'high-contrast':
      readOnlyTokens = {
        fg: dt.c.white,
        bg: dt.c.black,
        bd: dt.c.white
      };
      break;
  }
  return {
    ...renderDesignTokens({
      fg: dt.reportTokens.stateSecondaryToggle.normal.fg,
      bd: dt.reportTokens.stateSecondaryToggle.normal.bd,
      bg: dt.reportTokens.stateSecondaryToggle.normal.bg,
      'font-size-s': dt.reportTokens.font.size.s,
      'description-fg': dt.tokens['color-label-text']
    }, 'radio'),
    ...renderDesignTokens(dt.reportTokens.stateSecondaryToggle.selected, 'radio-selected'),
    ...renderDesignTokens(dt.reportTokens.stateSecondaryToggle.disabled, 'radio-disabled'),
    ...renderDesignTokens(dt.reportTokens.stateSecondaryToggle.disabledSelected, 'radio-disabled-selected'),
    ...renderDesignTokens(readOnlyTokens, 'radio-read-only')
  };
};

/**
 * @deprecated use `getRadioButtonDesignTokens()` instead
 */
export const getRadioButtonThemeTokens = dt => getRadioButtonDesignTokens(dt);
//# sourceMappingURL=RadioButton.js.map